import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Trash2, Check, Search, Mail, UserPlus } from "lucide-react";
import { ConfigField } from "config/adminConfig";
import leaveMailCcService, {
  AdminEmail,
  Recipient,
  normalizeRecipientList,
} from "services/AdminConfig/leaveMailCcService";
import HeaderButtons from "components/Button/HeaderButtons";
import InlineTable, { TableColumn, Spinner } from "components/InlineTable";
import GenericModal from "components/Modal";
import DeletePopup from "components/DeletePopup/deletePopup";
// NOTE: shared resolver + toaster, so save/delete report the API's own reason
// instead of a fixed string, and confirm success the user can see.
import { getApiErrorMessage } from "util/errorMessage";
import { useToaster } from "contexts/toasterContext";

// Two columns: recipient (name + email) and the row action (remove).
const COLUMNS: TableColumn[] = [
  { key: "recipient", header: "Recipient" },
  { key: "actions", header: "", width: 120 },
];

// Same row-slot height used across the AdminConfig inline tables so rows never jump.
const ROW_H = "h-[42px]";

interface LeaveRecipientConfigProps {
  field: ConfigField;
}

/**
 * Leave notification CC recipients.
 *
 * Admins added here receive employee leave-request emails. The picker shows
 * admin *names* (from GET /admin-emails) but the value persisted to the server
 * is always the admin *email*.
 *
 * The POST /leave-mail-cc-config endpoint APPENDS the given emails to the
 * existing list, so on save we send ONLY the newly selected emails (`pending`),
 * never the ones already saved.
 */
const LeaveRecipientConfig: React.FC<LeaveRecipientConfigProps> = ({ field }) => {
  const { addToast } = useToaster();

  // All admins that can be selected as recipients.
  const [admins, setAdmins] = useState<AdminEmail[]>([]);
  // Recipients already persisted on the server (carry the id needed for delete).
  const [savedRecipients, setSavedRecipients] = useState<Recipient[]>([]);
  // Emails selected in this session but not yet saved — the ONLY thing we POST.
  const [pendingEmails, setPendingEmails] = useState<string[]>([]);

  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [apiError, setApiError] = useState("");

  // Recipient pending delete confirmation (drives the confirm modal).
  const [deleteTarget, setDeleteTarget] = useState<Recipient | null>(null);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fast email -> displayName lookup for rendering recipients that may not
  // (or no longer) exist in the admin list.
  const nameByEmail = useMemo(() => {
    const map = new Map<string, string>();
    admins.forEach((a) => map.set(a.email, a.displayName));
    return map;
  }, [admins]);

  // ── Load admins + current recipients ──────────────────────────────────────
  const load = useCallback(async () => {
    setPageLoading(true);
    setApiError("");
    try {
      const [adminRes, recipientRes] = await Promise.all([
        leaveMailCcService.getAdminEmails(),
        leaveMailCcService.getRecipients(),
      ]);
      setAdmins(adminRes.data ?? []);
      setSavedRecipients(normalizeRecipientList(recipientRes.data));
      setPendingEmails([]);
    } catch (err) {
      // A load failure keeps the inline banner — nothing is on screen to act on.
      setApiError(getApiErrorMessage(err, "Failed to load recipients."));
    } finally {
      setPageLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Close the dropdown on outside click.
  useEffect(() => {
    if (!dropdownOpen) return;
    const onClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [dropdownOpen]);

  // Admins that are neither already saved nor pending, filtered by the search box.
  const availableAdmins = useMemo(() => {
    const taken = new Set([...savedRecipients.map((r) => r.email), ...pendingEmails]);
    const term = search.trim().toLowerCase();
    return admins.filter(
      (a) =>
        !taken.has(a.email) &&
        (!term ||
          a.displayName.toLowerCase().includes(term) ||
          a.email.toLowerCase().includes(term))
    );
  }, [admins, savedRecipients, pendingEmails, search]);

  const hasPending = pendingEmails.length > 0;
  const isBusy = pageLoading || saving || deletingId !== null;

  // ── Actions ────────────────────────────────────────────────────────────────
  const addRecipient = (email: string) => {
    setPendingEmails((prev) =>
      prev.includes(email) || savedRecipients.some((r) => r.email === email)
        ? prev
        : [...prev, email]
    );
    setApiError("");
    setSearch("");
    setDropdownOpen(false);
  };

  // Drop a not-yet-saved addition (local only, no API call).
  const removePending = (email: string) => {
    setPendingEmails((prev) => prev.filter((e) => e !== email));
    setApiError("");
  };

  const handleSave = async () => {
    if (!hasPending) return;
    setSaving(true);
    setApiError("");
    try {
      // Send ONLY the newly selected emails — the backend appends them.
      const addedCount = pendingEmails.length;
      await leaveMailCcService.saveRecipients(pendingEmails);
      setPendingEmails([]);
      // Reload so newly saved recipients come back with their server ids
      // (required for individual delete).
      await load();
      addToast(
        addedCount > 1
          ? `${addedCount} recipients added successfully`
          : "Recipient added successfully",
        "success",
      );
    } catch (err) {
      addToast(getApiErrorMessage(err, "Failed to save recipients."), "error");
    } finally {
      setSaving(false);
    }
  };

  // Delete a saved recipient by its server id.
  const handleDelete = async (id: number) => {
    setDeletingId(id);
    setApiError("");
    try {
      await leaveMailCcService.deleteRecipient(id);
      setSavedRecipients((prev) => prev.filter((r) => r.id !== id));
      addToast("Recipient deleted successfully", "success");
    } catch (err) {
      addToast(getApiErrorMessage(err, "Failed to delete recipient."), "error");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* ── Page header (fixed) ─────────────────────────────────────────── */}
      <div className="flex-shrink-0 flex items-center justify-between gap-3 pt-2 pb-4">
        {/* CHANGE: the back button that used to sit here was removed.
            ModuleConfig now renders one back/breadcrumb strip above EVERY
            module, so keeping this one produced two arrows on the same screen.
            It also called navigate(-1), which walks browser history and could
            leave the app when the page was opened from a direct link; the
            shared control navigates to the configuration landing explicitly. */}
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="min-w-0">
            <h1 className="text-base font-semibold text-gray-900 dark:text-white truncate">
              {field.label}
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              Admins who receive employee leave-request emails
            </p>
          </div>
        </div>

        {/* ── Add recipient (dropdown of admin names) ─────────────────────── */}
        <div ref={dropdownRef} className="relative flex items-center gap-2 mr-4 mt-1">
          <HeaderButtons
            size="small"
            onClick={() => setDropdownOpen((o) => !o)}
            disabled={isBusy}
            // icon={<Plus size={13} />}
          >
            Add Recipient
          </HeaderButtons>

          {dropdownOpen && (
            <div className="absolute right-0 top-full mt-2 w-72 z-20 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg overflow-hidden">
              {/* Search */}
              <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 dark:border-gray-700">
                <Search size={14} className="text-gray-400 flex-shrink-0" />
                <input
                  type="text"
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search admins…"
                  className="w-full bg-transparent text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none"
                />
              </div>

              {/* Options */}
              <div className="max-h-64 overflow-y-auto py-1">
                {availableAdmins.length === 0 ? (
                  <p className="px-3 py-4 text-center text-xs text-gray-400">
                    {admins.length === 0 ? "No admins found" : "All admins added"}
                  </p>
                ) : (
                  availableAdmins.map((a) => (
                    <button
                      key={a.userId}
                      type="button"
                      onClick={() => addRecipient(a.email)}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                    >
                      <span className="flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300">
                        <UserPlus size={13} />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                          {a.displayName}
                        </span>
                        <span className="block text-xs text-gray-400 truncate">{a.email}</span>
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Card ──────────────────────────────────────────────────────────── */}
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden bg-white dark:bg-gray-800 shadow-sm">
        {apiError && (
          <div className="flex-shrink-0 mx-5 mt-3 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-error text-sm font-medium">
            {apiError}
          </div>
        )}

        <InlineTable
          columns={COLUMNS}
          loading={pageLoading}
          emptyText="No recipients yet"
          emptySubText='Click "Add Recipient" above to add an admin'
          minWidth={0}
          className="overflow-x-hidden px-5"
        >
          {/* ── Saved recipients (persisted on the server; deletable) ────── */}
          {savedRecipients.map((recipient) => {
            const name = recipient.displayName || nameByEmail.get(recipient.email);
            const isDeleting = deletingId === recipient.id;
            return (
              <tr
                key={`saved-${recipient.id}`}
                className={`align-middle transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/30 ${
                  isDeleting ? "opacity-50" : ""
                }`}
              >
                <td className="px-3 py-2.5">
                  <div className={`flex items-center gap-2.5 ${ROW_H} min-w-0`}>
                    <span className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300">
                      <Mail size={14} />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                        {name || recipient.email}
                      </span>
                      {name && (
                        <span className="block text-xs text-gray-400 truncate">{recipient.email}</span>
                      )}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <div className={`flex items-center justify-end ${ROW_H}`}>
                    <button
                      type="button"
                      onClick={() => setDeleteTarget(recipient)}
                      disabled={isBusy || deletingId !== null}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {isDeleting ? <Spinner className="h-3 w-3 text-red-500" /> : <Trash2 size={11} />}
                      Remove
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}

          {/* ── Pending additions (removable; sent on save) ─────────────── */}
          {pendingEmails.map((email) => {
            const name = nameByEmail.get(email);
            return (
              <tr
                key={`pending-${email}`}
                className="align-middle border-l-2 border-blue-400 dark:border-blue-500 bg-blue-50/30 dark:bg-blue-900/5"
              >
                <td className="px-3 py-2.5">
                  <div className={`flex items-center gap-2.5 ${ROW_H} min-w-0`}>
                    <span className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300">
                      <Mail size={14} />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                        {name || email}
                      </span>
                      {name && (
                        <span className="block text-xs text-gray-400 truncate">{email}</span>
                      )}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <div className={`flex items-center justify-end ${ROW_H}`}>
                    <button
                      type="button"
                      onClick={() => removePending(email)}
                      disabled={isBusy}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Trash2 size={11} />
                      Remove
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </InlineTable>

        {/* ── Footer — only when there are newly selected recipients ─────── */}
        {hasPending && (
          <div className="flex-shrink-0 flex items-center justify-end gap-3 px-5 py-3.5 border-t border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/80">
            <button
              type="button"
              onClick={() => setPendingEmails([])}
              disabled={saving}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Discard
            </button>
            <HeaderButtons
              onClick={handleSave}
              loading={saving}
              disabled={saving}
              icon={<Check size={13} />}
            >
              Save {pendingEmails.length > 1 ? `${pendingEmails.length} Recipients` : "Recipient"}
            </HeaderButtons>
          </div>
        )}
      </div>

      {/* ── Delete confirmation ───────────────────────────────────────────── */}
      {deleteTarget && (
        <GenericModal
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          header="Delete Recipient"
        >
          <DeletePopup
            selectedRow={deleteTarget}
            onClose={() => setDeleteTarget(null)}
            handleDelete={(id) => {
              setDeleteTarget(null);
              handleDelete(id);
            }}
            message={`Are you sure you want to remove "${
              deleteTarget.displayName || nameByEmail.get(deleteTarget.email) || deleteTarget.email
            }" from the leave notification recipients?`}
          />
        </GenericModal>
      )}
    </div>
  );
};

export default LeaveRecipientConfig;
